import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  CreditCard,
  Calendar,
  ListChecks,
  LogOut,
  Menu,
  X,
  Settings,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Building,
  ShieldCheck,
  BarChart3,
  Send,
  FolderOpen,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useAgentAuth } from "../contexts/AgentAuthContext";
// import FloatingButtons from "./FloatingButtons"; // Temporarily commented out
import AnimatedIcon from "./whimsy/AnimatedIcon";
import FeedbackModal from "./shared/FeedbackModal";
import { COACH_NAVIGATION } from "../config/coachNavigation";

const COACH_NAV_ICONS = {
  dashboard: TrendingUp,
  agents: Users,
  accountability: ListChecks,
  pipeline: CreditCard,
  training: Calendar,
  resources: FolderOpen,
  settings: Settings,
};

const COACH_NAVIGATION_WITH_ICONS = COACH_NAVIGATION.map((item) => ({
  ...item,
  icon: COACH_NAV_ICONS[item.icon],
  type: "single",
}));

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { agent, isAgent, clearAgentAuth } = useAgentAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const isSuperAdmin = useMemo(() => {
    if (!user) return false;
    const normalizedRoles = Array.isArray(user.roles)
      ? user.roles.map((role) => role?.toLowerCase()).filter(Boolean)
      : [];
    const primaryRole = user.role?.toLowerCase();
    return (
      primaryRole === "super_admin" || normalizedRoles.includes("super_admin")
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNavigation([]);
      return;
    }

    if (isSuperAdmin) {
      setNavigation([
        {
          name: "Business Overview",
          href: "/super-admin/overview",
          icon: BarChart3,
          type: "single",
          description: "Platform performance snapshot",
        },
        {
          name: "Organizations",
          href: "/super-admin/organizations",
          icon: Building,
          type: "single",
          description: "Manage customer organizations",
        },
        {
          name: "User Directory",
          href: "/super-admin/users",
          icon: Users,
          type: "single",
          description: "Manage platform access",
        },
        {
          name: "Platform Health",
          href: "/super-admin/usage",
          icon: ShieldCheck,
          type: "single",
          description: "Adoption & usage analytics",
        },
      ]);
      return;
    }

    setNavigation(COACH_NAVIGATION_WITH_ICONS);
  }, [isSuperAdmin, user]);

  // Clear agent authentication when accessing coach-only routes
  useEffect(() => {
    // Define agent-specific routes
    const agentRoutes = ["/agent-dashboard", "/agent-portal"];

    // If we're not on an agent route and have agent auth, clear it
    const isAgentRoute = agentRoutes.some((route) =>
      location.pathname.startsWith(route)
    );

    if (!isAgentRoute && isAgent()) {
      console.log("Clearing agent auth for coach route:", location.pathname);
      clearAgentAuth();
    }
  }, [location.pathname, isAgent, clearAgentAuth]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
        // Load collapsed state from localStorage for desktop
        const savedCollapsed = localStorage.getItem("sidebarCollapsed");
        setSidebarCollapsed(savedCollapsed === "true");
      } else {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Save collapsed state to localStorage
  const toggleSidebarCollapse = () => {
    const newCollapsed = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsed);
    localStorage.setItem("sidebarCollapsed", newCollapsed.toString());
  };

  const handleNavClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Toggle expandable navigation sections (only one at a time)
  const toggleSection = (sectionName, forceOpen = false) => {
    setExpandedSections((prev) => {
      const isCurrentlyOpen = prev[sectionName];
      // If forcing open or toggling closed->open, close all others and open this one
      if (forceOpen || !isCurrentlyOpen) {
        return { [sectionName]: true };
      }
      // Otherwise toggle it closed
      return { [sectionName]: false };
    });
  };

  // Check if any child route is active (for group highlighting)
  const isGroupActive = (children) => {
    return children?.some((child) => {
      const isExact = location.pathname === child.href;
      const isNested = child.href !== "/" && location.pathname.startsWith(child.href + "/");
      return isExact || isNested;
    });
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-expand sections that contain active routes (only one at a time)
  // Close all sections when navigating to a non-group route
  useEffect(() => {
    const activeGroup = navigation.find(
      (item) => item.type === "group" && isGroupActive(item.children)
    );
    if (activeGroup) {
      setExpandedSections({ [activeGroup.name]: true });
    } else {
      // No active group, close all sections
      setExpandedSections({});
    }
  }, [location.pathname, navigation]);

  const getPageTitle = () => {
    if (location.pathname === "/instructors" || location.pathname.startsWith("/instructors/")) return "Training";
    // Check for Home first (now redirects to Dashboard)
    if (location.pathname === "/") {
      return isSuperAdmin ? "Business Overview" : "Dashboard";
    }

    // Check for Settings
    if (location.pathname === "/settings") return "Settings";

    // Check navigation items (including grouped items)
    for (const item of navigation) {
      // Skip section dividers
      if (item.type === "section") continue;

      // Check grouped items
      if (item.type === "group" && item.children) {
        for (const child of item.children) {
          if (location.pathname === child.href) {
            return child.name;
          }
          if (child.href !== "/" && location.pathname.startsWith(child.href + "/")) {
            return child.name;
          }
        }
        continue;
      }

      // Check single items
      if (location.pathname === item.href) {
        return item.name;
      }
      // Check if path starts with item href (for sub-pages)
      if (item.href && item.href !== "/" && location.pathname.startsWith(item.href)) {
        return item.name;
      }
    }

    if (isSuperAdmin && location.pathname.startsWith("/super-admin")) {
      return "Super Admin";
    }

    return "Home";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          id="mobile-overlay"
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <div
        id="sidebar"
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } fixed ${
          sidebarCollapsed && !isMobile ? "w-16" : "w-64"
        } h-screen transition-all duration-300 ease-in-out text-gray-700 flex flex-col z-50 md:translate-x-0 brand-shadow-lg brand-sidebar`}
      >
        {/* Sidebar Header */}
        <div
          id="sidebar-header"
          className={`flex items-center justify-between p-4 brand-sidebar-header ${
            sidebarCollapsed && !isMobile ? "px-2" : "px-4"
          }`}
        >
          {!sidebarCollapsed || isMobile ? (
            <h1 className="hellopc-wordmark text-xl select-none hellopc-logo">
              <span className="hello">Hello</span>
              <span className="pc">PC</span>
            </h1>
          ) : (
            <div
              className="hellopc-wordmark text-2xl mx-auto select-none hellopc-logo"
              title="HelloPC"
            >
              <span className="hello">H</span>
              <span className="pc">P</span>
            </div>
          )}
          {isMobile ? (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg brand-sidebar-button transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={toggleSidebarCollapse}
              className="p-2 rounded-lg brand-sidebar-button transition-colors"
              title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronLeft className="w-4 h-4" />
              )}
            </button>
          )}
        </div>

        {/* Navigation Links */}
        <nav
          id="sidebar-nav"
          className="flex-1 py-4 space-y-1 overflow-y-auto"
        >
          {navigation.map((item, index) => {
            // Section divider
            if (item.type === "section") {
              if (sidebarCollapsed && !isMobile) {
                return (
                  <div key={`section-${index}`} className="px-2 py-2">
                    <div className="border-t border-gray-600/30" />
                  </div>
                );
              }
              return (
                <div key={`section-${index}`} className="px-4 pt-4 pb-2">
                  <span className="text-xs font-semibold text-gray-400 tracking-wider">
                    {item.label}
                  </span>
                </div>
              );
            }

            // Expandable group
            if (item.type === "group") {
              const Icon = item.icon;
              const isExpanded = expandedSections[item.name];
              const hasActiveChild = isGroupActive(item.children);

              return (
                <div key={item.name} className={`px-2 ${sidebarCollapsed && !isMobile ? "px-1" : "px-2"}`}>
                  {/* Group header (clickable to navigate and expand) */}
                  <button
                    onClick={() => {
                      // Navigate to first child and expand section
                      navigate(item.children[0].href);
                      toggleSection(item.name, true);
                      handleNavClick();
                    }}
                    onMouseEnter={() => setHoveredItem(item.name)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className={`group flex items-center w-full px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                      hasActiveChild && !isExpanded
                        ? "brand-sidebar-active text-white shadow-lg"
                        : "text-gray-600 brand-sidebar-item-hover"
                    } ${sidebarCollapsed && !isMobile ? "justify-center px-2" : "justify-start"}`}
                    title={sidebarCollapsed && !isMobile ? item.description : ""}
                  >
                    <div className={`flex items-center transition-all duration-200 ${hasActiveChild && !isExpanded ? "" : "group-hover:scale-105"} ${sidebarCollapsed && !isMobile ? "justify-center" : "flex-1"}`}>
                      <AnimatedIcon
                        icon={Icon}
                        className={`w-5 h-5 ${sidebarCollapsed && !isMobile ? "" : "mr-3"} ${hasActiveChild && !isExpanded ? "text-white" : "text-gray-500 group-hover:text-brand-primary"}`}
                        isActive={hasActiveChild && !isExpanded}
                        isHovered={hoveredItem === item.name}
                      />
                      {(!sidebarCollapsed || isMobile) && (
                        <>
                          <span className="font-medium truncate">{item.name}</span>
                          <span className="ml-auto">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            )}
                          </span>
                        </>
                      )}
                    </div>
                    {/* Active indicator for collapsed group */}
                    {hasActiveChild && !isExpanded && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                    )}
                    {/* Tooltip for collapsed state with children list */}
                    {sidebarCollapsed && !isMobile && hoveredItem === item.name && (
                      <div className="brand-sidebar-tooltip absolute left-full ml-2 top-0 text-gray-700 bg-white border border-gray-200 px-2 py-2 rounded text-xs whitespace-nowrap z-50 shadow-lg">
                        <div className="font-semibold mb-1 pb-1 border-b border-gray-100">{item.name}</div>
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={handleNavClick}
                            className={`block py-1 px-1 rounded hover:bg-gray-100 ${location.pathname === child.href ? "text-brand-primary font-medium" : ""}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </button>

                  {/* Expanded children */}
                  {isExpanded && (!sidebarCollapsed || isMobile) && (
                    <div className="mt-1 ml-4 pl-3 border-l-2 border-gray-200 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        // Check for exact match
                        const isExactMatch = location.pathname === child.href;
                        // For nested routes, check if this is the most specific matching sibling
                        // (longest matching href wins)
                        const matchingSiblings = item.children.filter(
                          (sibling) => location.pathname === sibling.href ||
                            location.pathname.startsWith(sibling.href + "/")
                        );
                        const mostSpecificMatch = matchingSiblings.reduce(
                          (longest, current) => current.href.length > longest.href.length ? current : longest,
                          { href: "" }
                        );
                        const isChildActive = isExactMatch || mostSpecificMatch.href === child.href;

                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={handleNavClick}
                            onMouseEnter={() => setHoveredItem(child.name)}
                            onMouseLeave={() => setHoveredItem(null)}
                            className={`group flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 relative ${
                              isChildActive
                                ? "bg-brand-primary/10 text-brand-primary font-medium"
                                : "text-gray-600 hover:bg-gray-100"
                            }`}
                          >
                            <ChildIcon className={`w-4 h-4 mr-2 ${isChildActive ? "text-brand-primary" : "text-gray-400"}`} />
                            <span className="truncate">{child.name}</span>
                            {isChildActive && (
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-brand-primary rounded-r-full" />
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Single navigation item
            const Icon = item.icon;
            const isExactMatch = location.pathname === item.href;
            const isHomeActive = item.isHome && location.pathname === "/";
            const isNestedActive =
              !item.isHome &&
              item.href !== "/" &&
              location.pathname.startsWith(item.href + "/");
            const isItemActive = isExactMatch || isHomeActive || isNestedActive ||
              (item.href === "/classes" && (location.pathname === "/instructors" || location.pathname.startsWith("/instructors/")));

            return (
              <div
                key={item.name}
                className={`px-2 ${sidebarCollapsed && !isMobile ? "px-1" : "px-2"}`}
              >
                <Link
                  to={item.href}
                  onClick={handleNavClick}
                  onMouseEnter={() => setHoveredItem(item.name)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                    isItemActive
                      ? "brand-sidebar-active text-white shadow-lg"
                      : "text-gray-600 brand-sidebar-item-hover"
                  } ${sidebarCollapsed && !isMobile ? "justify-center px-2" : "justify-start"}`}
                  title={sidebarCollapsed && !isMobile ? item.description : ""}
                >
                  <div
                    className={`flex items-center transition-all duration-200 ${isItemActive ? "" : "group-hover:scale-105"} ${sidebarCollapsed && !isMobile ? "justify-center" : "flex-1"}`}
                  >
                    <AnimatedIcon
                      icon={Icon}
                      className={`w-5 h-5 ${sidebarCollapsed && !isMobile ? "" : "mr-3"} ${isItemActive ? "text-white" : "text-gray-500 group-hover:text-brand-primary"}`}
                      isActive={isItemActive}
                      isHovered={hoveredItem === item.name}
                      type={item.isHome ? "home" : "default"}
                    />
                    {(!sidebarCollapsed || isMobile) && (
                      <span className="font-medium truncate">{item.name}</span>
                    )}
                  </div>
                  {item.alertCount > 0 && (
                    <span
                      className={`${
                        sidebarCollapsed && !isMobile
                          ? "absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"
                          : "ml-auto inline-flex items-center justify-center rounded-full text-xs font-semibold px-2 py-0.5"
                      } ${
                        !sidebarCollapsed || isMobile
                          ? isItemActive
                            ? "bg-white text-brand-primary"
                            : "bg-red-100 text-red-700"
                          : ""
                      }`}
                    >
                      {(!sidebarCollapsed || isMobile) && (item.alertCount > 99 ? "99+" : item.alertCount)}
                    </span>
                  )}
                  {isItemActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                  )}
                  {sidebarCollapsed && !isMobile && hoveredItem === item.name && (
                    <div className="brand-sidebar-tooltip absolute left-full ml-2 top-1/2 -translate-y-1/2 text-gray-700 bg-white border border-gray-200 px-2 py-1 rounded text-xs whitespace-nowrap z-50 shadow-lg">
                      {item.name}
                      <div className="brand-sidebar-tooltip-arrow absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent"></div>
                    </div>
                  )}
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User Section */}
        <div
          id="sidebar-user"
          className={`brand-sidebar-user-section ${
            sidebarCollapsed && !isMobile ? "p-2" : "p-4"
          }`}
        >
          {/* Feedback Button */}
          <button
            onClick={() => setFeedbackModalOpen(true)}
            className={`flex items-center w-full px-3 py-2 text-sm rounded-lg transition-all duration-200 text-gray-600 brand-sidebar-item-hover mb-2 ${
              sidebarCollapsed && !isMobile ? "justify-center px-2" : "justify-start"
            }`}
            title={sidebarCollapsed && !isMobile ? "Feedback" : ""}
            id="feedback-button"
          >
            <Send className={`w-4 h-4 ${sidebarCollapsed && !isMobile ? "" : "mr-2"}`} />
            {(!sidebarCollapsed || isMobile) && "Feedback"}
          </button>

          {/* Divider */}
          <div className="border-t border-gray-600/30 my-2" />

          {/* User Menu with Dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`flex items-center w-full px-2 py-2 rounded-lg transition-all duration-200 hover:bg-gray-700/30 ${
                sidebarCollapsed && !isMobile ? "justify-center" : ""
              }`}
            >
              <div className="w-8 h-8 brand-gradient rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
              {(!sidebarCollapsed || isMobile) && (
                <>
                  <div className="flex-1 min-w-0 ml-2 text-left">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {isAgent()
                        ? agent?.name || agent?.email
                        : user?.fullName || user?.email}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                </>
              )}
            </button>

            {/* Dropdown Menu */}
            {userMenuOpen && (
              <div
                className={`absolute bottom-full mb-2 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 ${
                  sidebarCollapsed && !isMobile ? "left-full ml-2 -bottom-2" : "left-0 right-0"
                }`}
              >
                <Link
                  to="/settings"
                  onClick={() => {
                    handleNavClick();
                    setUserMenuOpen(false);
                  }}
                  className={`flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors ${
                    location.pathname === "/settings" || location.pathname.startsWith("/settings/")
                      ? "bg-brand-primary/10 text-brand-primary"
                      : ""
                  }`}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Link>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div
        className={`flex-1 flex flex-col w-full transition-all duration-300 ${
          isMobile ? "md:ml-64" : sidebarCollapsed ? "md:ml-16" : "md:ml-64"
        }`}
      >
        {/* Top Header Bar */}
        <header
          id="top-header"
          className="bg-white border-b border-slate-200 px-4 md:px-6 py-4 brand-shadow-sm"
          style={{
            background:
              "linear-gradient(135deg, #ffffff 0%, var(--brand-neutral-50) 100%)",
          }}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors md:hidden mr-3 brand-focus relative"
              >
                <Menu className="w-5 h-5 text-slate-600" />
              </button>
              <div className="flex items-center space-x-3">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                  {getPageTitle()}
                </h2>
                <div className="hidden md:block w-1 h-6 bg-gradient-to-b from-transparent via-slate-300 to-transparent"></div>
                <span className="hidden md:inline-block text-sm text-slate-500 font-medium">
                  Sidekick for Productivity Coaches
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-xs text-slate-500 hidden sm:block">
                Welcome back,{" "}
                <span className="font-medium text-slate-700">
                  {isAgent()
                    ? agent?.name?.split(" ")[0] || "Agent"
                    : user?.fullName?.split(" ")[0] || "Coach"}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main
          id="main-content"
          className="flex-1 p-4 md:p-6 overflow-y-auto bg-slate-50"
          style={{
            background:
              "linear-gradient(135deg, var(--brand-neutral-50) 0%, #ffffff 50%, var(--brand-neutral-50) 100%)",
          }}
        >
          <div className="brand-fade-in">{children}</div>
        </main>
      </div>

      {/* Floating Buttons (hide on dashboard to avoid duplicate chat) */}
      {/* Temporarily commented out - will add back later */}
      {/* {location.pathname !== "/" && <FloatingButtons />} */}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </div>
  );
};

export default Layout;
